import { For, Show, createSignal } from "solid-js";
import { ChangeJoinCodeModal } from "./ChangeJoinCodeModal";
import { ClassCard } from "./ClassCard";
import { ClassFormModal } from "./ClassFormModal";
import { ClassTeamModal } from "./ClassTeamModal";
import { DeleteClassModal } from "./DeleteClassModal";
import type { ClassesModule, TeacherClass } from "./classes";
import type { ClassTeamModule } from "./classTeam";
import { Icon } from "~/ui/Icon";

export function ClassesPage(props: {
  classes: TeacherClass[];
  module: ClassesModule;
  onReload: () => Promise<TeacherClass[]>;
  onSelect: (value: TeacherClass) => void;
  onRemember: (value: TeacherClass | undefined) => void;
  settingsMode?: boolean;
  teamModule?: ClassTeamModule;
}) {
  const [creating, setCreating] = createSignal(false);
  const [editing, setEditing] = createSignal<TeacherClass>();
  const [deleting, setDeleting] = createSignal<TeacherClass>();
  const [changingCode, setChangingCode] = createSignal<TeacherClass>();
  const [teamOf, setTeamOf] = createSignal<TeacherClass>();

  const create = async (draft: Parameters<ClassesModule["create"]>[0]) => {
    const value = await props.module.create(draft);
    const values = await props.onReload();
    setCreating(false);
    props.onRemember(values.find((item) => item.id === value.id) ?? value);
  };
  const update = async (value: TeacherClass, draft: Parameters<ClassesModule["update"]>[1]) => {
    await props.module.update(value.id, draft);
    await props.onReload();
    setEditing(undefined);
  };
  const remove = async (value: TeacherClass) => {
    await props.module.remove(value.id);
    const remaining = await props.onReload();
    setDeleting(undefined);
    props.onRemember(remaining[0]);
  };

  const changeCode = async (value: TeacherClass, newCode: string) => {
    await props.module.changeJoinCode(value.id, newCode);
    await props.onReload();
    setChangingCode(undefined);
  };

  return (
    <>
      <div class="card-header" style={{ "margin-bottom": "20px" }}>
        {props.settingsMode
          ? <h2 style={{ "font-size": "1.1rem", "font-weight": 700 }}><Icon name="book" style={{ width: "18px", height: "18px" }} /> Meine Klassen</h2>
          : <h1>Meine Klassen</h1>}
        <button class="btn btn-primary" type="button" onClick={() => setCreating(true)}>+ Neue Klasse</button>
      </div>
      <div class="grid-3">
        <For each={props.classes}>{(value) => (
          <ClassCard value={value} onSelect={() => props.onSelect(value)} onEdit={() => setEditing(value)} onDelete={() => setDeleting(value)} onChangeCode={() => setChangingCode(value)} onTeam={props.teamModule && value.school_id ? () => setTeamOf(value) : undefined} />
        )}</For>
      </div>
      <Show when={props.settingsMode && props.classes.length === 0}>
        <div class="card" style={{ "text-align": "center", padding: "40px" }}>
          <div style={{ "font-size": "3rem", "margin-bottom": "12px" }}><Icon name="book" style={{ width: "48px", height: "48px" }} /></div>
          <h3>Noch keine Klasse</h3>
          <p style={{ color: "var(--text-muted)", "margin-bottom": "16px" }}>Erstelle deine erste Klasse.</p>
          <button class="btn btn-primary" type="button" onClick={() => setCreating(true)}>+ Neue Klasse erstellen</button>
        </div>
      </Show>
      <Show when={creating()}><ClassFormModal onClose={() => setCreating(false)} onSave={create} /></Show>
      <Show when={editing()}>{(value) => <ClassFormModal value={value()} onClose={() => setEditing(undefined)} onSave={(draft) => update(value(), draft)} />}</Show>
      <Show when={deleting()}>{(value) => <DeleteClassModal value={value()} onClose={() => setDeleting(undefined)} onConfirm={() => remove(value())} />}</Show>
      <Show when={teamOf() && props.teamModule}>{(_) => <ClassTeamModal value={teamOf()!} module={props.teamModule!} onClose={() => setTeamOf(undefined)} onChanged={async () => { const values = await props.onReload(); setTeamOf(values.find((item) => item.id === teamOf()?.id)); }} />}</Show>
      <Show when={changingCode()}>{(value) => <ChangeJoinCodeModal value={value()} onClose={() => setChangingCode(undefined)} onSave={(code) => changeCode(value(), code)} />}</Show>
    </>
  );
}
